import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { format } from "date-fns";
import { toast } from "sonner";
import FileAttachments from "../components/FileAttachments";
import { Attachment } from "../types";

import {
  ArrowLeft,
  FileText,
  Save,
  Send,
  Calendar,
  AlertCircle,
  Info,
  BookOpenCheck,
  Sparkles,
  Wand2,
  Loader2
} from "lucide-react";
import { cn } from "../components/ui/utils";

export default function PolicyEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { getPolicyById, updatePolicy, updatePolicyInState, users, policyAssignments, refreshPolicies, approvers, fetchApprovers } = useData();

  const [formData, setFormData] = useState({
    title: "",
    policyNumber: "",
    description: "",
    effectiveDate: "",
    expiryDate: "",
    reviewDate: "",
    approvedBy: "",
    requireSignature: false,
    status: "Draft" as "Draft" | "Active"
  });

  // Dual version tracking state
  const [versionInfo, setVersionInfo] = useState({
    publishedVersion: null as number | null,
    draftVersion: null as number | null,
    hasDraft: false,
    hasUnpublishedChanges: false
  });

  const [isEditingDraft, setIsEditingDraft] = useState(false);
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showPublishDialog, setShowPublishDialog] = useState(false);

  // AI Enhancement State
  const [aiState, setAiState] = useState({
    isLoading: false,
    showPreview: false,
    currentField: '',
    currentAction: '',
    result: '',
    originalValue: ''
  });

  // Add state to track if policy has been previously signed
  const [hasBeenSigned, setHasBeenSigned] = useState(false);

  // Fetch policy directly from backend
  const fetchPolicy = async (policyId: string) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('Please log in to edit policies');
      }

      const response = await fetch(`${API_BASE}/api/policies/${policyId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Your session has expired. Please log in again.');
        }
        if (response.status === 403) {
          throw new Error('You do not have permission to edit this policy.');
        }
        if (response.status === 404) {
          throw new Error('Policy not found. It may have been deleted.');
        }

        const errorData = await response.json();
        throw new Error(errorData.message || 'Unable to load policy for editing');
      }

      const data = await response.json();
      if (data.success && data.policy) {
        setFormData({
          title: data.policy.title || "",
          policyNumber: data.policy.policyNumber || "",
          description: data.policy.description || "",
          effectiveDate: data.policy.effectiveDate?.split('T')[0] || "",
          expiryDate: data.policy.expiryDate?.split('T')[0] || "",
          reviewDate: data.policy.reviewDate?.split('T')[0] || "",
          approvedBy: (typeof data.policy.approvedBy === 'object' && data.policy.approvedBy?.id) ? data.policy.approvedBy.id : data.policy.approvedBy || "",
          requireSignature: !!data.policy.requireSignature,
          status: data.policy.status || "Draft"
        });

        // Set version information for dual version tracking
        setVersionInfo({
          publishedVersion: data.policy.publishedVersion || null,
          draftVersion: data.policy.draftVersion || null,
          hasDraft: data.policy.hasDraft || false,
          hasUnpublishedChanges: data.policy.hasUnpublishedChanges || false
        });

        // Determine if we're editing a draft
        setIsEditingDraft(data.policy.hasDraft || false);
        setContent(data.policy.content || "");
        setAttachments(data.policy.attachments || []);

        // Check if policy has been signed before
        const hasSigns = policyAssignments.some(assignment =>
          assignment.policy?.id == policyId && assignment.signatureType
        );
        setHasBeenSigned(hasSigns);

        // Block editing of active policies
        if (data.policy.status === 'Active') {
          toast.error('This policy is currently active and cannot be edited. Please duplicate it to create a new version.');
          navigate(`/policies/${policyId}`);
          return;
        }
      } else {
        throw new Error('Policy data not found in response');
      }
    } catch (error) {
      console.error('Error fetching policy:', error);

      // Try to get policy from context as fallback
      const policyFromContext = getPolicyById(policyId);
      if (policyFromContext) {
        console.log('Using policy from context as fallback');
        setFormData({
          title: policyFromContext.title || "",
          policyNumber: policyFromContext.policyNumber || "",
          description: policyFromContext.description || "",
          effectiveDate: policyFromContext.effectiveDate?.split('T')[0] || "",
          expiryDate: policyFromContext.expiryDate?.split('T')[0] || "",
          reviewDate: policyFromContext.reviewDate?.split('T')[0] || "",
          approvedBy: (policyFromContext.approvedBy as any)?.id || policyFromContext.approvedBy || "",
          requireSignature: !!policyFromContext.requireSignature,
          status: (policyFromContext.status === 'Active' || policyFromContext.status === 'Draft') ? policyFromContext.status : "Draft"
        });
        setContent(policyFromContext.content || "");
        setAttachments(policyFromContext.attachments || []);
        setIsLoading(false);
        return;
      }

      const errorMessage = error instanceof Error ? error.message : 'Unable to load policy for editing. Please try again.';
      toast.error(errorMessage);
      navigate('/policies');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchPolicy(id);
    }

    if (approvers.length === 0) {
      fetchApprovers();
    }
  }, [id, currentUser, navigate]);

  // Filter users for policy approvers (Admin and Director only)
  const getEligibleApprovers = () => {
    return users.filter(user =>
      !user.archived
    );
  };

  const handleAiAccept = () => {
    const { currentField, result } = aiState;

    switch (currentField) {
      case 'title':
        setFormData(prev => ({ ...prev, title: result }));
        break;
      case 'description':
        setFormData(prev => ({ ...prev, description: result }));
        break;
      case 'content':
        setContent(result);
        break;
    }

    setAiState({
      isLoading: false,
      showPreview: false,
      currentField: '',
      currentAction: '',
      result: '',
      originalValue: ''
    });

    toast.success('AI enhancement applied successfully');
  };

  const handleAiCancel = () => {
    setAiState({
      isLoading: false,
      showPreview: false,
      currentField: '',
      currentAction: '',
      result: '',
      originalValue: ''
    });
  };

  const validateForm = (isDraft = false) => {
    const newErrors: Record<string, string> = {};

    if (!isDraft) {
      if (!formData.title.trim()) newErrors.title = "Title is required";
      if (formData.title.length > 64) newErrors.title = "Title must be 64 characters or less";
      if (!formData.description.trim()) newErrors.description = "Description is required";
      if (!formData.effectiveDate) newErrors.effectiveDate = "Effective date is required";
      if (!formData.approvedBy) newErrors.approvedBy = "Approved by is required";
      if (!content.trim()) newErrors.content = "Content is required";

      // Validate dates - allow today's date
      if (formData.effectiveDate) {
        const effectiveDate = new Date(formData.effectiveDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to beginning of day
        if (effectiveDate < today) {
          newErrors.effectiveDate = "Effective date cannot be in the past";
        }
      }

      if (formData.expiryDate && formData.effectiveDate &&
        new Date(formData.expiryDate) <= new Date(formData.effectiveDate)) {
        newErrors.expiryDate = "Expiry date must be after effective date";
      }

      if (formData.reviewDate && formData.effectiveDate &&
        new Date(formData.reviewDate) <= new Date(formData.effectiveDate)) {
        newErrors.reviewDate = "Review date must be after effective date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveDraft = async () => {
    if (!id) return;

    const isValid = validateForm(true); // Draft validation (less strict)
    if (!isValid) return;

    setIsProcessing(true);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
      const endpoint = `/api/policies/${id}`;

      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          title: formData.title.trim(),
          policyNumber: formData.policyNumber.trim() || undefined,
          description: formData.description.trim(),
          content: content.trim(),
          requireSignature: formData.requireSignature,
          effectiveDate: formData.effectiveDate,
          expiryDate: formData.expiryDate || undefined,
          reviewDate: formData.reviewDate || undefined,
          approvedBy: formData.approvedBy || undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save draft');
      }

      const data = await response.json();

      toast.success('Draft saved successfully!');

      // Update the global state with the updated policy
      if (id && data.data) {
        updatePolicyInState(id, data.data);
      }

      // Refresh policies to ensure user data is properly populated
      await refreshPolicies();

      // Update the form data with the response to ensure consistency
      if (data.data) {
        setFormData({
          title: data.data.title || "",
          policyNumber: data.data.policyNumber || "",
          description: data.data.description || "",
          effectiveDate: data.data.effectiveDate?.split('T')[0] || "",
          expiryDate: data.data.expiryDate?.split('T')[0] || "",
          reviewDate: data.data.reviewDate?.split('T')[0] || "",
          approvedBy: data.data.approvedBy?.id || "",
          requireSignature: !!data.data.requireSignature,
          status: data.data.status || "Draft"
        });
        setContent(data.data.content || "");
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save draft');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePublish = async () => {
    if (!id) return;

    const isValid = validateForm(false); // Full validation for publishing
    if (!isValid) return;

    setIsProcessing(true);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

      // Update the policy with status 'Active' to publish it
      const response = await fetch(`${API_BASE}/api/policies/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          title: formData.title.trim(),
          policyNumber: formData.policyNumber.trim() || undefined,
          description: formData.description.trim(),
          content: content.trim(),
          requireSignature: formData.requireSignature,
          effectiveDate: formData.effectiveDate,
          expiryDate: formData.expiryDate || undefined,
          reviewDate: formData.reviewDate || undefined,
          approvedBy: formData.approvedBy || undefined,
          status: 'Active' // Set status to Active to publish
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to publish policy');
      }

      const data = await response.json();

      toast.success('Policy published successfully!');

      // Update the global state with the updated policy
      if (id && data.data) {
        updatePolicyInState(id, data.data);
      }

      // Refresh policies to ensure user data is properly populated
      await refreshPolicies();

      navigate(`/policies/${id}`);
    } catch (error) {
      console.error('Error publishing policy:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to publish policy');
    } finally {
      setIsProcessing(false);
      setShowPublishDialog(false);
    }
  };

  const handleDiscardDraft = async () => {
    if (!id) return;

    // Since we don't have a discard endpoint, just reload the original policy
    setIsProcessing(true);

    try {
      // Just refresh the policy from the server, which will reset any unsaved changes
      await fetchPolicy(id);

      // Refresh policies to ensure user data is properly populated
      await refreshPolicies();

      toast.success('Draft discarded successfully!');
    } catch (error) {
      console.error('Error discarding draft:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to discard draft');
    } finally {
      setIsProcessing(false);
    }
  };



  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p>Loading policy...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/policies/${id}`}>
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold">Edit Policy</h1>
              <p className="text-muted-foreground">Make changes to the policy content and settings</p>
            </div>
            <div className="flex items-center gap-2">
              {versionInfo.publishedVersion && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  v{versionInfo.publishedVersion} Published
                </span>
              )}
              {versionInfo.hasDraft && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  v{versionInfo.draftVersion} Draft
                </span>
              )}
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${formData.status === 'Active'
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
                }`}>
                {formData.status}
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          {versionInfo.hasDraft && (
            <Button variant="outline" onClick={handleDiscardDraft} disabled={isProcessing}>
              <AlertCircle className="h-4 w-4 mr-2" />
              {isProcessing ? "Discarding..." : "Discard Draft"}
            </Button>
          )}
          <Button variant="outline" onClick={handleSaveDraft} disabled={isProcessing}>
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Draft
              </>
            )}
          </Button>
          <Button onClick={() => setShowPublishDialog(true)} disabled={isProcessing}>
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Publishing...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Save & Publish
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Dual Version System Information Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="space-y-2">
              <h4 className="font-medium text-blue-900">Editing Policy Versions</h4>
              <div className="text-sm text-blue-700 space-y-1">
                <p><strong>Save Draft:</strong> Saves your changes as a draft version. Changes won't be visible to users until published.</p>
                <p><strong>Save & Publish:</strong> Publishes your changes immediately, making them active for all users.</p>
                {versionInfo.hasDraft && (
                  <p><strong>Discard Draft:</strong> Reverts all draft changes back to the published version.</p>
                )}
                <p className="text-blue-600 font-medium">
                  💡 {versionInfo.hasDraft
                    ? "You have unpublished changes. Users will see the published version until you publish your draft."
                    : "You're editing the published version. Any changes will be saved as drafts until published."}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Policy Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter policy title"
                  className={errors.title ? "border-red-500" : ""}
                  maxLength={64}
                />
                <p className={`text-sm text-right ${formData.title.length >= 60 ? 'text-red-500' :
                  formData.title.length >= 50 ? 'text-amber-500' :
                    'text-muted-foreground'
                  }`}>
                  {formData.title.length}/64 characters
                </p>
                {errors.title && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.title}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="policyNumber">Policy Number</Label>
                <Input
                  id="policyNumber"
                  value={formData.policyNumber}
                  onChange={(e) => setFormData({ ...formData, policyNumber: e.target.value })}
                  placeholder="Enter policy number (optional)"
                  className={errors.policyNumber ? "border-red-500" : ""}
                />
                <p className="text-sm text-muted-foreground">
                  Optional unique identifier for this policy
                </p>
                {errors.policyNumber && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.policyNumber}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the policy"
                  rows={3}
                  className={errors.description ? "border-red-500" : ""}
                />
                {errors.description && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.description}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="approvedBy">Approved By *</Label>
                  <Select value={formData.approvedBy} onValueChange={(value) => setFormData({ ...formData, approvedBy: value })}>
                    <SelectTrigger className={errors.approvedBy ? "border-red-500" : ""}>
                      <SelectValue placeholder="Select approver" />
                    </SelectTrigger>
                    <SelectContent>
                      {approvers.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name} ({user.userRole})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    Available roles: Admin, Director
                  </p>
                  {errors.approvedBy && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.approvedBy}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="requireSignature">Require Digital Signature *</Label>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="requireSignature"
                      checked={formData.requireSignature}
                      onCheckedChange={(checked) => setFormData({ ...formData, requireSignature: checked })}
                    />
                    <span>{formData.requireSignature ? "Yes" : "No"}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Choose whether users need to digitally sign this policy
                  </p>
                  {errors.requireSignature && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.requireSignature}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Policy Content / Instructions *</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Textarea
                  id="content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Enter the policy content here..."
                  rows={10}
                  className={errors.content ? "border-red-500" : ""}
                />
                {errors.content && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.content}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Attachments Section */}
          <FileAttachments
            resourceType="policies"
            resourceId={id || ''}
            attachments={attachments}
            onAttachmentsChange={setAttachments}
          />

        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Schedule</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="effectiveDate">Effective Date *</Label>
                <Input
                  id="effectiveDate"
                  type="date"
                  value={formData.effectiveDate}
                  onChange={(e) => setFormData({ ...formData, effectiveDate: e.target.value })}
                  className={errors.effectiveDate ? "border-red-500" : ""}
                />
                {errors.effectiveDate && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.effectiveDate}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="expiryDate">Expiry Date (optional)</Label>
                <Input
                  id="expiryDate"
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                  className={errors.expiryDate ? "border-red-500" : ""}
                  disabled={!formData.effectiveDate}
                  min={formData.effectiveDate || undefined}
                />
                {!formData.effectiveDate && (
                  <p className="text-sm text-muted-foreground">
                    Set an effective date first to enable expiry date selection
                  </p>
                )}
                {errors.expiryDate && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.expiryDate}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="reviewDate">Review Date (optional)</Label>
                <Input
                  id="reviewDate"
                  type="date"
                  value={formData.reviewDate}
                  onChange={(e) => setFormData({ ...formData, reviewDate: e.target.value })}
                  className={errors.reviewDate ? "border-red-500" : ""}
                  disabled={!formData.effectiveDate}
                  min={formData.effectiveDate || undefined}
                />
                {!formData.effectiveDate && (
                  <p className="text-sm text-muted-foreground">
                    Set an effective date first to enable review date selection
                  </p>
                )}
                {errors.reviewDate && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.reviewDate}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-4 w-4" />
                Assignment Note
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  This policy will be updated without changing existing assignments. Use the "Policy Assignments"
                  section to manage policy assignments after saving.
                </p>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-blue-700 font-medium">Next Steps:</p>
                  <ol className="list-decimal list-inside text-blue-600 mt-1 space-y-1">
                    <li>Save the policy changes</li>
                    <li>Go to Policy Assignments tab</li>
                    <li>Manage assignments for users or departments</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* AI Enhancement Preview Modal */}
      <Dialog open={aiState.showPreview} onOpenChange={handleAiCancel}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              AI Enhancement Preview
            </DialogTitle>
            <DialogDescription>
              Review the AI-generated {aiState.currentAction === 'polish' ? 'polished' : 'generated'} {aiState.currentField} and choose to accept or cancel.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Original:</Label>
              <div className="mt-1 p-3 bg-muted/50 rounded-md border">
                {aiState.currentField === 'content' ? (
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: aiState.originalValue || '<p>No content</p>' }} />
                ) : (
                  <p className="text-sm">{aiState.originalValue || `No ${aiState.currentField}`}</p>
                )}
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium text-green-600">AI Enhanced:</Label>
              <div className="mt-1 p-3 bg-green-50 rounded-md border border-green-200">
                {aiState.currentField === 'content' ? (
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: aiState.result }} />
                ) : (
                  <p className="text-sm">{aiState.result}</p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleAiCancel}>
              Cancel
            </Button>
            <Button onClick={handleAiAccept} className="bg-green-600 hover:bg-green-700">
              Accept Enhancement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-orange-500" />
              Publish Policy
            </DialogTitle>
            <DialogDescription>
              You are about to publish this policy.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-md">
              <div className="space-y-2 text-sm">
                <p className="font-medium text-orange-800">Important Notice:</p>
                <ul className="space-y-1 text-orange-700">
                  <li>• You will not be able to edit it after publishing</li>
                  <li>• If you're unsure, save the policy as a draft</li>
                  <li>• To assign the policy for signature, you need to publish it</li>
                  <li>• If you want to make changes after publishing, you'll need to duplicate the published version to create a new version of this policy</li>
                </ul>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublishDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handlePublish} disabled={isProcessing}>
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Publishing...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Publish Policy
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 